import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import { FormattedMessage, useIntl } from 'react-intl'
import Input, { InputType } from 'components/shared/Input'
import { InvoiceSection, AddressRow } from './styles'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import {
    InvoiceField,
    setInvoiceInfo,
    toggleOrderInfoAdditionalModule,
} from 'store/order-slice'
import { useResolutions } from 'store/resolutions'
import { useErrorMessages } from 'utils/hooks/useErrorMessages'
import Link from 'components/shared/Link'
import Checkbox from 'components/shared/Checkbox'
import Info from 'components/shared/Info'
import { useEffect } from 'react'

const Invoice = () => {
    const { formatMessage } = useIntl()
    const { invoice, personalInvoice } = useAppSelector((state) => state.order)
    const { isChecked, postCode } = invoice
    const {
        isChecked: isPersonalInvoiceChecked,
        postCode: isPersonalInvoicePostCode,
    } = personalInvoice
    const dispatch = useAppDispatch()
    const { getError, handleRemoveError } = useErrorMessages()
    const { isTablet } = useResolutions()

    const handleInputChange = (field: InvoiceField, value: string) => {
        dispatch(setInvoiceInfo({ field, value }))
    }

    const toggleInvoiceInfo = () => {
        if (isChecked) {
            handleInputChange('nip', '')
            handleInputChange('street', '')
            handleInputChange('apartment', '')
            handleInputChange('city', '')
            handleInputChange('companyName', '')
            handleInputChange('postCode', '')
        }

        dispatch(
            toggleOrderInfoAdditionalModule({
                field: 'invoice',
                isChecked: !isChecked,
            })
        )

        dispatch(
            toggleOrderInfoAdditionalModule({
                field: 'personalInvoice',
                isChecked: false,
            })
        )
    }
    const togglePersonalInvoiceInfo = () => {
        if (isPersonalInvoiceChecked) {
            handleInputChange('street', '')
            handleInputChange('apartment', '')
            handleInputChange('city', '')
            handleInputChange('postCode', '')
        }

        dispatch(
            toggleOrderInfoAdditionalModule({
                field: 'personalInvoice',
                isChecked: !isPersonalInvoiceChecked,
            })
        )
    }

    const renderField = ({
        id,
        label,
        placeholder,
        type,
        maxLength,
        maxWidth,
        isOptional = false,
        isSmallerLabel = false,
    }: {
        id: InvoiceField
        label: string
        placeholder?: string
        type?: InputType
        maxLength?: number
        maxWidth?: number
        isOptional?: boolean
        isSmallerLabel?: boolean
    }) => (
        <Input
            type={type || 'text'}
            id={`orderBillingData.${id}`}
            onBlurChange={(value) => handleInputChange(id, value)}
            onValueChange={(value) => {
                handleRemoveError(`orderBillingData.${id}`)
                handleInputChange(id, value)
            }}
            label={label}
            placeholder={placeholder}
            isError={!!getError(`orderBillingData.${id}`)}
            errorMessage={getError(`orderBillingData.${id}`)}
            hasNoMaxWidth={!isTablet}
            maxLength={maxLength}
            maxWidth={maxWidth}
            isOptional={isOptional}
            isSmallerLabel={isSmallerLabel}
            testId={`payment-field-${
                isPersonalInvoiceChecked ? 'private' : 'company'
            }-invoice-${id}`}
        />
    )

    const unmaskValue = (value: string) =>
        value.replaceAll('-', '').replaceAll('_', '')

    const isPersonalInvoiceError =
        !!getError('orderBillingData.postCode') ||
        !!getError('orderBillingData.city') ||
        !!getError('orderBillingData.street')

    useEffect(() => {
        handleRemoveError('orderBillingData.postCode')
        handleRemoveError('orderBillingData.city')
        handleRemoveError('orderBillingData.street')
        handleRemoveError('orderBillingData.appartment')
    }, [isPersonalInvoiceChecked])

    return (
        <>
            <Box gap={16}>
                <Box
                    direction={['column', 'row']}
                    justify="space-between"
                    align="flex-start"
                    isFullWidth={true}
                    gap={32}
                >
                    <Box direction="row" align="center" gap={24}>
                        <Checkbox
                            isChecked={isChecked}
                            setIsChecked={() => {
                                toggleInvoiceInfo()
                            }}
                            testId="payment-check-company-invoice"
                        >
                            <Text variant="body" color="grayscale_900">
                                <FormattedMessage
                                    id="order.invoice.company_switch_label"
                                    defaultMessage="Chcę fakturę na firmę"
                                />
                            </Text>
                        </Checkbox>
                    </Box>
                </Box>

                {!isChecked && (
                    <Text variant="bodySmall" color="grayscale_700">
                        <FormattedMessage
                            id="order.invoice.personal_invoice_information"
                            defaultMessage="Jeśli nie prowadzisz działalności gospodarczej lub innej spółki, automatycznie wystawimy Ci fakturę imienną. "
                        />
                        <br />
                        {
                            <Link
                                variant="gray"
                                textVariant="bodySmall"
                                icon={null}
                                isUnderlined
                                onClick={() => togglePersonalInvoiceInfo()}
                                data-testid={`payment-toggle-private-invoice-${
                                    isPersonalInvoiceChecked ? 'hide' : 'show'
                                }`}
                            >
                                {!isPersonalInvoiceChecked ? (
                                    <FormattedMessage
                                        id="order.invoice.personal_invoice_request_add"
                                        defaultMessage="Dodaj adres do faktury imiennej"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="order.invoice.personal_invoice_request_remove"
                                        defaultMessage="Usun adres do faktury imiennej"
                                    />
                                )}
                            </Link>
                        }
                    </Text>
                )}
            </Box>

            {isChecked && (
                <>
                    <InvoiceSection
                        gap={[32, 32]}
                        pt={[48, 16]}
                        isFullWidth={true}
                    >
                        {renderField({
                            id: 'companyName',
                            label: `${formatMessage({
                                id: 'order.invoice.company_name_label',
                                defaultMessage: 'Nazwa firmy',
                            })}*`,
                            placeholder: formatMessage({
                                id: 'order.invoice.company_name_placeholder',
                                defaultMessage: 'Nazwa firmy',
                            }),
                            maxLength: 100,
                            isSmallerLabel: false,
                        })}
                        {renderField({
                            id: 'nip',
                            label: `${formatMessage({
                                id: 'order.invoice.nip_label',
                                defaultMessage: 'NIP',
                            })}*`,
                            placeholder: formatMessage({
                                id: 'order.invoice.nip_placeholder',
                                defaultMessage: 'NIP',
                            }),
                            maxLength: 20,
                            isSmallerLabel: true,
                        })}
                    </InvoiceSection>
                </>
            )}

            {(isPersonalInvoiceChecked || isChecked) && (
                <>
                    {isPersonalInvoiceChecked && isPersonalInvoiceError && (
                        <Box mt={32}>
                            <Info color="error">
                                <FormattedMessage
                                    id="order.invoice.personal_invoice_error"
                                    defaultMessage="Wypełnij wszystkie pola adresowe lub <span>usuń adres z faktury imiennej</span>"
                                    values={{
                                        span: (chunks: string[]) => (
                                            <Link
                                                variant="error"
                                                textVariant="bodySmall"
                                                icon={null}
                                                isUnderlined
                                                onClick={() =>
                                                    togglePersonalInvoiceInfo()
                                                }
                                            >
                                                {chunks}
                                            </Link>
                                        ),
                                    }}
                                />
                            </Info>
                        </Box>
                    )}

                    <InvoiceSection
                        gap={32}
                        pt={32}
                        isFullWidth={true}
                        direction="column"
                    >
                        <AddressRow
                            direction={['column', 'row']}
                            gap={[32, 24]}
                            justify="flex-start"
                            align="flex-end"
                        >
                            {renderField({
                                id: 'street',
                                label: `${formatMessage({
                                    id: 'order.invoice.street_and_number_label',
                                    defaultMessage: 'Ulica i numer budynku',
                                })}`,
                                placeholder: formatMessage({
                                    id: 'order.invoice.street_and_number_placeholder',
                                    defaultMessage: 'Ulica i numer budynku',
                                }),
                                maxWidth: 370,
                            })}
                            {renderField({
                                id: 'apartment',
                                label: formatMessage({
                                    id: 'order.invoice.apartment_label',
                                    defaultMessage: 'Numer lokalu',
                                }),
                                placeholder: formatMessage({
                                    id: 'order.invoice.apartment_placeholder',
                                    defaultMessage: 'Numer lokalu',
                                }),
                                isOptional: true,
                                maxWidth: 200,
                            })}
                        </AddressRow>
                        <AddressRow
                            direction={['column-reverse', 'row-reverse']}
                            justify="flex-end"
                            gap={[32, 24]}
                        >
                            {renderField({
                                id: 'city',
                                label: `${formatMessage({
                                    id: 'order.invoice.city_label',
                                    defaultMessage: 'Miasto',
                                })}`,
                                placeholder: formatMessage({
                                    id: 'order.invoice.city_placeholder',
                                    defaultMessage: 'Miasto',
                                }),
                                maxWidth: 370,
                            })}

                            <Input
                                type="text"
                                mask={'##-###'}
                                maskPlaceholder={'_'}
                                id={'postCode'}
                                value={
                                    isChecked
                                        ? postCode
                                        : isPersonalInvoicePostCode
                                }
                                label={`${formatMessage({
                                    id: 'order.invoice.zip_code_label',
                                    defaultMessage: 'Kod pocztowy',
                                })}`}
                                isError={
                                    !!getError('orderBillingData.postCode')
                                }
                                errorMessage={getError(
                                    'orderBillingData.postCode'
                                )}
                                hasNoMaxWidth={!isTablet}
                                onValueChange={(value) => {
                                    handleRemoveError(
                                        'orderBillingData.postCode'
                                    )
                                    handleInputChange(
                                        'postCode',
                                        unmaskValue(value)
                                    )
                                }}
                                maxWidth={200}
                                testId="payment-field-contact-postCode"
                            />
                        </AddressRow>
                    </InvoiceSection>
                </>
            )}
        </>
    )
}

export default Invoice
